import { cn } from '@goaltree/ui/lib/utils'
import { type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { showsTimeMeter, timeMeter } from '@/lib/time-meter'

type MeterTask = Parameters<typeof showsTimeMeter>[0] & { _creationTime?: number }

/** Elapsed-time meter for an open task with a due date; null when it doesn't apply. */
export function useTaskTimeMeter(task: MeterTask) {
  const { dueDate, _creationTime: start } = task
  const eligible = showsTimeMeter(task) && start != null
  return useMemo(
    () => (eligible ? timeMeter(start, dueDate!, Date.now()) : null),
    // Day granularity: recompute when the task changes or the local day rolls over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eligible, start, dueDate, new Date().toDateString()],
  )
}

const FILL = {
  normal: 'bg-primary/45',
  amber: 'bg-amber-500/70 dark:bg-amber-400/70',
  red: 'bg-red-500/70 dark:bg-red-400/70',
} as const

type Meter = NonNullable<ReturnType<typeof useTaskTimeMeter>>

function useMeterLabel(meter: Meter | null) {
  const { t } = useTranslation()
  if (!meter) return undefined
  return meter.overdue
    ? t('taskMeter.overdue', { total: meter.total })
    : t('taskMeter.dayOf', { day: meter.day, total: meter.total })
}

export function TimeMeterBar({ meter, className }: { meter: Meter; className?: string }) {
  const label = useMeterLabel(meter)
  return (
    <div
      role="img"
      aria-label={label}
      className={cn('pointer-events-none absolute inset-x-3 bottom-[3px] h-[3px]', className)}
    >
      {meter.continuous ? (
        <div className="relative size-full overflow-hidden rounded-full bg-muted-foreground/15">
          <div
            className={cn('h-full rounded-full', FILL[meter.tone])}
            style={{ width: `${meter.ratio * 100}%` }}
          />
          {meter.ticks.map((p) => (
            <span
              key={p}
              className="absolute inset-y-0 w-px bg-background/80"
              style={{ left: `${p * 100}%` }}
            />
          ))}
        </div>
      ) : (
        <div className="flex size-full gap-px">
          {Array.from({ length: meter.total }, (_, i) => (
            <span
              key={i}
              className={cn(
                'h-full min-w-0 flex-1 rounded-[1px]',
                i < meter.filled ? FILL[meter.tone] : 'bg-muted-foreground/15',
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Title with a faint 1px line across the first `ratio` of its text; gradients flow across wrapped lines. */
export function ElapsedTitle({ meter, children }: { meter: Meter | null; children: ReactNode }) {
  const label = useMeterLabel(meter)
  if (!meter) return <>{children}</>
  // Capped so the title never reads as fully struck (i.e. done).
  const pct = `${Math.min(meter.ratio, 0.85) * 100}%`
  const line = 'color-mix(in srgb, currentColor 35%, transparent)'
  return (
    <span
      title={label}
      style={{
        backgroundImage: `linear-gradient(to right, ${line} ${pct}, transparent ${pct})`,
        backgroundSize: '100% 1px',
        backgroundPosition: '0 58%',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {children}
    </span>
  )
}
